<div class="space-y-6">
  <!-- Header with Post Action for Instructors -->
  <div class="flex items-center justify-between gap-4">
    <div>
      <h3 class="text-lg font-bold text-base-content flex items-center gap-2">
        <i class="fa-solid fa-bullhorn text-primary"></i>
        <span>Class Announcements</span>
      </h3>
      <p class="text-xs text-base-content/60 mt-0.5">
        Updates, reminders, and notices sent directly to students.
      </p>
    </div>
    @if (isInstructor) {
      <button
        type="button"
        (click)="openAnnouncementModal()"
        class="daisy-btn daisy-btn-primary daisy-btn-sm gap-1.5 shrink-0"
      >
        <i class="fa-solid fa-plus text-xs"></i>
        <span>Post Announcement</span>
      </button>
    }
  </div>

  <!-- Loading State -->
  @if (isLoading) {
    <div class="flex justify-center items-center py-12">
      <span class="daisy-loading daisy-loading-spinner daisy-loading-md text-primary"></span>
    </div>
  } @else if (announcements.length === 0) {
    <!-- Empty State -->
    <div class="daisy-card daisy-card-bordered bg-base-100 p-8 text-center border-base-200">
      <div class="w-12 h-12 rounded-full bg-base-200/80 flex items-center justify-center mx-auto mb-3 text-base-content/40">
        <i class="fa-solid fa-bullhorn text-lg"></i>
      </div>
      <h4 class="font-semibold text-sm text-base-content">No announcements yet</h4>
      <p class="text-xs text-base-content/50 mt-1 max-w-sm mx-auto">
        @if (isInstructor) {
          Keep your students informed about schedule changes, tests, and homework by posting an announcement.
        } @else {
          Your instructor hasn't posted any announcements for this class yet.
        }
      </p>
      @if (isInstructor) {
        <div class="mt-4">
          <button
            type="button"
            (click)="openAnnouncementModal()"
            class="daisy-btn daisy-btn-primary daisy-btn-sm gap-1.5"
          >
            <i class="fa-solid fa-plus text-xs"></i>
            <span>Create First Announcement</span>
          </button>
        </div>
      }
    </div>
  } @else {
    <!-- Announcement List -->
    <div class="space-y-4">
      @for (item of announcements; track item._id) {
        <div
          class="daisy-card daisy-card-bordered bg-base-100 shadow-sm transition-all border-base-200"
          [class.border-primary/40]="item.pinned"
          [class.bg-primary/5]="item.pinned"
        >
          <div class="daisy-card-body p-4 sm:p-5">
            <div class="flex items-start justify-between gap-3">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-full bg-base-200 flex items-center justify-center text-primary font-bold text-xs shrink-0 overflow-hidden">
                  @if (item.author.profile?.personal?.profileImage || item.author.avatar) {
                    <img
                      [src]="item.author.profile?.personal?.profileImage || item.author.avatar"
                      [alt]="getAuthorDisplayName(item.author)"
                      class="w-full h-full object-cover"
                    />
                  } @else {
                    <i class="fa-solid fa-user-tie text-sm"></i>
                  }
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <span class="font-semibold text-xs text-base-content">
                      {{ getAuthorDisplayName(item.author) }}
                    </span>
                    @if (item.pinned) {
                      <span class="daisy-badge daisy-badge-primary daisy-badge-xs font-semibold gap-1">
                        <i class="fa-solid fa-thumbtack text-[10px]"></i>
                        <span>Pinned</span>
                      </span>
                    }
                  </div>
                  <span class="text-[11px] text-base-content/50">
                    {{ item.createdAt | date: 'medium' }}
                  </span>
                </div>
              </div>

              @if (isInstructor) {
                <button
                  type="button"
                  (click)="deleteAnnouncement(item._id)"
                  class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error hover:bg-error/10 p-1"
                  title="Delete announcement"
                >
                  <i class="fa-solid fa-trash text-xs"></i>
                </button>
              }
            </div>

            <h4 class="font-bold text-sm sm:text-base text-base-content mt-3">
              {{ item.title }}
            </h4>
            <p class="text-xs sm:text-sm text-base-content/80 mt-1 whitespace-pre-wrap leading-relaxed">
              {{ item.message }}
            </p>
          </div>
        </div>
      }
    </div>
  }
</div>

<!-- Announcement Modal -->
@if (showModal) {
  <div class="daisy-modal daisy-modal-open z-50">
    <div class="daisy-modal-box max-w-lg bg-base-100 p-6 rounded-2xl shadow-2xl border border-base-200">
      <div class="flex items-center justify-between pb-3 border-b border-base-200">
        <h3 class="font-bold text-base sm:text-lg text-base-content flex items-center gap-2">
          <i class="fa-solid fa-bullhorn text-primary"></i>
          <span>New Class Announcement</span>
        </h3>
        <button
          type="button"
          (click)="closeAnnouncementModal()"
          class="daisy-btn daisy-btn-ghost daisy-btn-xs daisy-btn-circle"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="py-4 space-y-4 text-xs sm:text-sm">
        <div>
          <label class="block font-semibold text-base-content mb-1">
            Announcement Title <span class="text-error">*</span>
          </label>
          <input
            type="text"
            [(ngModel)]="announcementForm.title"
            placeholder="e.g. Tomorrow's live session moved to 6 PM"
            class="daisy-input daisy-input-bordered daisy-input-sm w-full"
          />
        </div>

        <div>
          <label class="block font-semibold text-base-content mb-1">
            Message <span class="text-error">*</span>
          </label>
          <textarea
            rows="4"
            [(ngModel)]="announcementForm.message"
            placeholder="Type your announcement details here..."
            class="daisy-textarea daisy-textarea-bordered daisy-textarea-sm w-full leading-relaxed"
          ></textarea>
        </div>

        <div class="flex items-center justify-between p-3 rounded-xl bg-base-200/50 border border-base-200">
          <div>
            <p class="font-semibold text-base-content">Pin Announcement</p>
            <p class="text-[11px] text-base-content/60">Keep this announcement at the top of the feed</p>
          </div>
          <input
            type="checkbox"
            [(ngModel)]="announcementForm.pinned"
            class="daisy-toggle daisy-toggle-primary daisy-toggle-sm"
          />
        </div>

        <div class="daisy-alert bg-info/10 border border-info/20 text-[11px] text-base-content/80 p-3 rounded-xl flex items-start gap-2">
          <i class="fa-solid fa-circle-info text-info mt-0.5"></i>
          <span>This announcement will trigger an instant notification to all active enrolled students via web push and in-app alerts.</span>
        </div>
      </div>

      <div class="daisy-modal-action pt-3 border-t border-base-200 flex items-center justify-end gap-2">
        <button
          type="button"
          (click)="closeAnnouncementModal()"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm font-medium"
        >
          Cancel
        </button>
        <button
          type="button"
          (click)="postAnnouncement()"
          [disabled]="isPosting || !announcementForm.title.trim() || !announcementForm.message.trim()"
          class="daisy-btn daisy-btn-primary daisy-btn-sm font-medium gap-1.5"
        >
          @if (isPosting) {
            <span class="daisy-loading daisy-loading-spinner daisy-loading-xs"></span>
          }
          <i class="fa-solid fa-paper-plane text-xs"></i>
          <span>Publish & Notify</span>
        </button>
      </div>
    </div>
    <div class="daisy-modal-backdrop" (click)="closeAnnouncementModal()"></div>
  </div>
}
